import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { useDrawingArea, useYScale } from "@mui/x-charts-pro";
import ChartTooltip from "components/Dashboard/ChartTooltip";
import React, { useMemo, useState } from "react";

/**
 * Shared, unit-agnostic threshold rendering for both the Waste Shipments
 * (lb-denominated) and Waste Generated (kg-denominated) facility charts.
 *
 * Each threshold maps to:
 *   - a dashed horizontal `<line>` at its y-position
 *   - a filled dot on the y-axis
 *   - a tooltip row listing every threshold (the hovered one bolded)
 *
 * Thresholds at visually-coincident y-positions are staggered by
 * {@link STAGGER_PX} so each color stays readable.
 *
 * **Position math note:** `useYScale` returns absolute SVG y coordinates
 * (mapping the data domain → the same pixel space the y-axis ticks use). We
 * therefore use the scale output directly and do NOT add `top` from
 * `useDrawingArea`. Both consumers (margin.top = 0 in shipments, margin.top >
 * 0 in generated) render correctly with this approach.
 */

/** Minimum vertical gap between rendered threshold lines / dots. */
const STAGGER_PX = 3;

/**
 * Generic axis threshold shape consumed by the shared components. Tiles map
 * their tile-specific threshold types to this shape before passing in.
 */
export type AxisThreshold = {
  /** Stable identifier, used for hover matching in the tooltip. */
  id: string;
  /** Display name shown in the tooltip (e.g. `"Federal LQG"`). */
  name: string;
  /** Numeric value in the chart's y-axis units (lb or kg, whatever the tile picks). */
  value: number;
  /** Line / dot color. */
  color: string;
  /** Pre-formatted value string for the tooltip row (e.g. `"100 kg"` or `"2,200 lbs"`). */
  displayValue: string;
};

type PositionedThreshold = {
  threshold: AxisThreshold;
  y: number;
};

/**
 * For each threshold, compute its rendered y position from the kg/lb → pixel
 * scale, then walk top-to-bottom and push any threshold within `STAGGER_PX`
 * of the previous one down so each stays visually distinct.
 */
function usePositionedThresholds(thresholds: AxisThreshold[]): PositionedThreshold[] {
  const yScale = useYScale("y-axis");
  return useMemo(() => {
    if (!yScale) return [];
    const positioned = thresholds
      .map<{ threshold: AxisThreshold; naturalY: number } | null>((threshold) => {
        const scaled = yScale(threshold.value);
        return scaled == null ? null : { threshold, naturalY: scaled };
      })
      .filter((entry): entry is { threshold: AxisThreshold; naturalY: number } => entry !== null)
      .sort((a, b) => a.naturalY - b.naturalY);

    let previousY: number | null = null;
    return positioned.map(({ threshold, naturalY }) => {
      let y = naturalY;
      if (previousY !== null && y - previousY < STAGGER_PX) {
        y = previousY + STAGGER_PX;
      }
      previousY = y;
      return { threshold, y };
    });
  }, [thresholds, yScale]);
}

/**
 * Dashed horizontal reference lines, one per threshold. Lines at visually
 * coincident values are staggered (see {@link STAGGER_PX}) so each color stays
 * readable.
 */
export function ThresholdReferenceLines({ thresholds }: { thresholds: AxisThreshold[] }) {
  const { left, top, width, height } = useDrawingArea();
  const positioned = usePositionedThresholds(thresholds);

  return (
    <g>
      {positioned.map(({ threshold, y }) => {
        if (y < top || y > top + height) return null;
        return (
          <line
            key={`threshold-line-${threshold.id}`}
            x1={left}
            x2={left + width}
            y1={y}
            y2={y}
            stroke={threshold.color}
            strokeDasharray="5,5"
            strokeWidth={2}
            style={{ pointerEvents: "none" }}
          />
        );
      })}
    </g>
  );
}

/**
 * Filled dots on the y-axis, one per threshold. Hovering a dot opens a tooltip
 * listing every threshold (with the hovered one bolded); hovering the y-axis
 * itself shows the same list with no row bolded.
 */
export function ThresholdYAxisIndicators({ thresholds }: { thresholds: AxisThreshold[] }) {
  const { left, top, height } = useDrawingArea();
  const positioned = usePositionedThresholds(thresholds);

  const [showTooltip, setShowTooltip] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  const handleAxisEnter = (event: React.MouseEvent) => {
    setShowTooltip(true);
    setHoveredId(null);
    setMouse({ x: event.clientX, y: event.clientY });
  };
  const handleDotEnter = (id: string, event: React.MouseEvent) => {
    setShowTooltip(true);
    setHoveredId(id);
    setMouse({ x: event.clientX, y: event.clientY });
  };
  const handleLeave = () => {
    setShowTooltip(false);
    setHoveredId(null);
  };
  const handleMove = (event: React.MouseEvent) => {
    if (showTooltip) setMouse({ x: event.clientX, y: event.clientY });
  };

  return (
    <>
      <g>
        <rect
          x={left - 60}
          y={top - 20}
          width={60}
          height={height + 40}
          fill="transparent"
          style={{ pointerEvents: "all", cursor: "default" }}
          onMouseEnter={handleAxisEnter}
          onMouseLeave={handleLeave}
          onMouseMove={handleMove}
        />
        {positioned.map(({ threshold, y }) => {
          if (y < top || y > top + height) return null;
          return (
            <g key={`y-axis-threshold-${threshold.id}`}>
              <circle
                cx={left - 10}
                cy={y}
                r={4}
                fill={threshold.color}
                stroke="white"
                strokeWidth={1}
                style={{ pointerEvents: "all" }}
                onMouseEnter={(e) => handleDotEnter(threshold.id, e)}
                onMouseLeave={handleLeave}
                onMouseMove={handleMove}
              />
              <circle
                cx={left - 10}
                cy={y}
                r={12}
                fill="transparent"
                style={{ pointerEvents: "all" }}
                onMouseEnter={(e) => handleDotEnter(threshold.id, e)}
                onMouseLeave={handleLeave}
                onMouseMove={handleMove}
              />
            </g>
          );
        })}
      </g>
      {showTooltip && (
        <Tooltip
          open={true}
          title={<ThresholdAxisTooltip thresholds={thresholds} hoveredId={hoveredId} />}
          placement="right"
          slotProps={{
            popper: {
              anchorEl: {
                getBoundingClientRect: () => ({
                  top: mouse.y,
                  left: mouse.x,
                  right: mouse.x,
                  bottom: mouse.y,
                  width: 0,
                  height: 0,
                  x: mouse.x,
                  y: mouse.y,
                  toJSON: () => ({}),
                }),
              },
              style: { pointerEvents: "none" },
            },
            tooltip: {
              sx: { maxWidth: "none", backgroundColor: "transparent", p: 0 },
            },
          }}
        >
          <span style={{ position: "fixed", top: mouse.y, left: mouse.x, pointerEvents: "none" }} />
        </Tooltip>
      )}
    </>
  );
}

function ThresholdAxisTooltip({
  thresholds,
  hoveredId,
}: {
  thresholds: AxisThreshold[];
  hoveredId: string | null;
}) {
  const sorted = [...thresholds].sort((a, b) => b.value - a.value);
  return (
    <ChartTooltip title="Generator Thresholds">
      <Stack direction="column" gap={1}>
        {sorted.map((t) => {
          const isHovered = hoveredId === t.id;
          return (
            <Stack direction="row" gap={0.5} key={t.id} alignItems="center">
              <Box
                sx={{
                  width: 20,
                  height: 2,
                  display: "inline-block",
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    height: "2px",
                    background: `repeating-linear-gradient(to right, ${t.color} 0, ${t.color} 3px, transparent 2px, transparent 5px)`,
                  },
                }}
              />
              <Typography variant="body2" fontWeight={isHovered ? 700 : 400}>
                {t.name}
              </Typography>
              <Typography variant="body2" fontWeight={isHovered ? 700 : 400}>
                ({t.displayValue})
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </ChartTooltip>
  );
}
